<template>
  <div class="state-dropdown-select-classification">
    <p v-if="title" class="title">{{ $t(title) }}<span v-if="required" class="red">*</span></p>
    <ValidationProvider
      v-slot="{ errors }"
      class="state-dropdown-select-classification_content-wrap"
      :rules="rules"
    >
      <div
        v-click-outside="dropdownClose"
        class="state-dropdown-select-classification_content"
        :class="{ outline: outline, disabled: disabled, error: errors.length }"
      >
        <i v-if="leadingIcon" class="icon leading-icon" :class="leadingIcon" />
        <button @click="onclick()">
          <p :class="{ active: value && value.length > 0 }">
            {{ _showText }}
          </p>
          <span v-if="_allBtnIcon === 'icon-indeterminate-check-box'">{{ value.length }}</span>
        </button>
        <i
          v-if="outline"
          class="icon trailing-icon outline icon-chevron-up"
          :class="{ show: dropdown_menu_show }"
        />
        <i v-if="disabled" class="icon trailing-icon icon-chevron-down" />
        <i
          v-else-if="!outline && !dropdown_menu_show"
          class="icon trailing-icon icon-arrow-drop-down-circle"
        />
        <i
          v-else-if="!outline && dropdown_menu_show"
          class="icon trailing-icon icon-arrow-drop-up-circle"
        />
        <transition mode="out-in">
          <div v-if="dropdown_menu_show" class="dropdown-menu">
            <SearchInput
              v-if="hasSearch"
              :value="searchValue"
              placeholder="搜尋"
              class="pl-8 pr-8"
              @input="$_onSearch($event)"
            />
            <div class="dropdown-scrollable-content">
              <div v-if="menu && menu.length > 0 && !searchValue.trim()">
                <button @click="$_onSelectAll">
                  <i :class="_allBtnIcon" /> {{ $t(allSelectText) }}
                </button>
              </div>
              <perfect-scrollbar class="dropdown-scrollable-content_list ps">
                <LvText v-if="!_filteredMenu.length" h6 class="ml-20">{{ $t('無選項') }}</LvText>
                <div v-for="(firstLayer, firstLayerKey) in _filteredMenu" :key="firstLayerKey">
                  <!-- First Layer -->
                  <div class="classification-title" @click="onFirstLayerClick(firstLayer)">
                    <!-- <span>{{ getFirstLayerStatus(firstLayer.menu,value) }}</span> -->
                    <i :class="getFirstLayerStatus(firstLayer.menu, value)" />
                    <span>{{ $t(firstLayer.title) }}</span>
                  </div>
                  <div class="classification-subtitle">
                    <!-- Second Layer -->
                    <div
                      v-for="(secondLayer, secondLayerKey) in firstLayer.menu"
                      :key="secondLayerKey"
                    >
                      <label>
                        <input
                          type="checkbox"
                          :checked="value && value.includes(secondLayer.value)"
                          @change="onChange($event, secondLayer.value)"
                        />
                        <div class="checkbox-icon">
                          <i class="checked icon-check-box" />
                          <i class="unchecked icon-check-box-outline-blank" />
                        </div>
                        <span>{{ $t(secondLayer.text) }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </perfect-scrollbar>
            </div>
          </div>
        </transition>
      </div>
      <div v-if="errors && errors.length > 0" class="invalid-text">
        <i class="icon-warning invalid-icon" />
        <p v-for="(text, textIndex) in errors" :key="textIndex" class="text">
          {{ $t(text) }}
        </p>
      </div>
      <input type="hidden" :value="value" />
    </ValidationProvider>
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside';
import { ValidationProvider } from 'vee-validate';
import { extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
extend('required', {
  ...required,
  message: '此項目為必填',
});
export default {
  name: 'StateDropdownSelectClassification',
  directives: {
    ClickOutside,
  },
  components: {
    ValidationProvider,
  },
  props: {
    menu: {
      type: Array,
      default() {
        return [];
      },
    },
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    placeholder: {
      type: String,
      default() {
        return this.$t('全部');
      },
    },
    leadingIcon: {
      type: String,
      default: '',
    },
    outline: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    rules: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    allSelectText: {
      type: String,
      default() {
        return this.$t('全部');
      },
    },
    hasSearch: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    dropdown_menu_show: false,
    templateContent: null,
    allBtnIcon: 'icon-check-box',
    searchValue: '',
  }),
  computed: {
    _value() {
      if (this.value) {
        return this.value;
      } else {
        return [];
      }
    },
    _allValue() {
      if (!this.menu || this.menu.length === 0) return [];
      return this.menu.flatMap(menu => menu.menu);
    },
    _showText() {
      if (!this.menu || this.menu.length === 0) return this.$t('無選項');
      if (this._value.length === 0) return this.$t(this.placeholder);

      // 使用 Set 加速查找，從 O(n*m) 優化為 O(n+m)
      const valueSet = new Set(this._value);
      const allValueSet = new Set(this._allValue.map(item => item.value));

      // 檢查是否有任何選中的值存在於選項中
      const hasValidValue = this._value.some(val => allValueSet.has(val));
      if (!hasValidValue) return this.$t(this.placeholder);

      // 如果全部選中
      if (this._value.length === this._allValue.length) {
        return this.$t(this.allSelectText) || this.$t('全部');
      }

      // 過濾並顯示選中的項目文字
      const selectedTexts = this._allValue
        .filter(item => valueSet.has(item.value))
        .map(item => item.text);

      return selectedTexts.join('、');
    },
    _allBtnIcon() {
      if (this._value.length === 0) return 'icon-check-box-outline-blank';
      const allValueSet = new Set(this._allValue.map(item => item.value));
      const hasValidValue = this._value.some(val => allValueSet.has(val));
      if (!hasValidValue) return 'icon-check-box-outline-blank';
      if (this._value.length < this._allValue.length) return 'icon-indeterminate-check-box';
      return 'icon-check-box';
    },
    _filteredMenu() {
      const keyword = this.searchValue.trim().toLowerCase();
      if (!keyword) {
        return this.menu;
      }
      return this.$_filterMenu(this.menu, keyword);
    },
  },
  methods: {
    onclick() {
      if (this.disabled) {
        return;
      }
      this.toggleDropdownMenuShow();
    },
    toggleDropdownMenuShow() {
      if (this.dropdown_menu_show) {
        this.searchValue = '';
      }
      this.dropdown_menu_show = !this.dropdown_menu_show;
    },
    dropdownClose() {
      this.dropdown_menu_show = false;
      this.searchValue = '';
    },
    $_onSearch(value) {
      this.searchValue = value;
    },
    $_matchTitle(title, keyword) {
      return this.$t(title).toLowerCase().includes(keyword);
    },
    $_matchText(text, keyword) {
      return this.$t(text).toLowerCase().includes(keyword);
    },
    $_filterMenu(menu, keyword) {
      return (menu || []).reduce((acc, firstLayer) => {
        if (this.$_matchTitle(firstLayer.title, keyword)) {
          if (firstLayer.menu && firstLayer.menu.length) {
            acc.push(firstLayer);
          }
          return acc;
        }
        const filteredMenu = (firstLayer.menu || []).filter(item =>
          this.$_matchText(item.text, keyword),
        );
        if (filteredMenu.length > 0) {
          acc.push({ ...firstLayer, menu: filteredMenu });
        }
        return acc;
      }, []);
    },
    isValueInSameLayer(value) {
      let inSameLayer = true;
      value.forEach(valueItemA => {
        const layerTitleA = this.getLayerTitleByValue(valueItemA);
        value.forEach(valueItemB => {
          const layerTitleB = this.getLayerTitleByValue(valueItemB);
          if (layerTitleA != layerTitleB) {
            inSameLayer = false;
          }
        });
      });
      return inSameLayer;
    },
    getLayerTitleByValue(value) {
      let title = '';
      this.menu.forEach(firstLayer => {
        firstLayer.menu.forEach(secondLayer => {
          if (secondLayer.value == value) {
            title = firstLayer.title;
          }
        });
      });
      return title;
    },
    findTextByValue(value) {
      let text = '';
      this.menu.forEach(firstLayer => {
        firstLayer.menu.forEach(secondLayer => {
          if (secondLayer.value == value) {
            text = secondLayer.text;
          }
        });
      });
      return text;
    },
    $_onSelectAll() {
      const inputValue =
        this._value.length < this._allValue.length ? this._allValue.map(item => item.value) : [];
      this.$emit('input', inputValue);
      this.$emit('click');
    },
    onFirstLayerClick(firstLayer) {
      const layerStatus = this.getFirstLayerStatus(firstLayer.menu, this._value);
      const tarArr = [];
      firstLayer.menu.forEach(menuItem => {
        tarArr.push(menuItem.value);
      });
      let newValue = JSON.parse(JSON.stringify(this._value));
      if (
        layerStatus == 'icon-check-box-outline-blank' ||
        layerStatus == 'icon-indeterminate-check-box'
      ) {
        newValue = newValue.concat(tarArr);
        newValue = newValue.filter((item, pos) => newValue.indexOf(item) === pos);
      } else if (layerStatus == 'icon-check-box') {
        newValue = newValue.filter(el => {
          return !tarArr.includes(el);
        });
      }
      this.$emit('input', newValue);
      this.$emit('click');
    },
    onChange($event, value) {
      let newValue = JSON.parse(JSON.stringify(this._value));
      if ($event.target.checked) {
        if (newValue && !newValue.includes(value)) {
          newValue.push(value);
        }
      } else {
        newValue = newValue.filter(item => item !== value);
      }
      this.$emit('input', newValue);
      this.$emit('click');
    },
    getFirstLayerStatus(layerMenu) {
      const tarArr = [];
      layerMenu.forEach(menuItem => {
        tarArr.push(menuItem.value);
      });
      if (tarArr.every(v => this._value.includes(v))) {
        return 'icon-check-box';
      } else if (tarArr.some(r => this._value.includes(r))) {
        return 'icon-indeterminate-check-box';
      } else {
        return 'icon-check-box-outline-blank';
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/component/state-dropdown-select-classification';
</style>
